import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TEMPLATE_CHANNEL_DEFS, type TemplateChannel } from '@goyone/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, Modal, PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';

/**
 * الرسائل المحفوظة — مكتبةُ ما يُكتب كثيرًا.
 *
 * «ابعتلي العرض» و«مواعيد العمل» و«رابط الاجتماع» تُكتب مرةً هنا
 * وتُدرَج بنقرة في كل مكانٍ يُرسَل منه: مُرسِل الملف السريع، وشات
 * قنوات ميتا، وكونسول الاتصال — لكل قناةٍ رسائلُها.
 */

interface Template {
  id: string;
  name: string;
  channel: TemplateChannel;
  subject: string | null;
  body: string;
  isActive: boolean;
  order: number;
}

export function SavedMessages() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('MANAGER');
  const [editing, setEditing] = useState<Template | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['saved-messages'],
    queryFn: () => api<{ items: Template[] }>('/callcenter/templates'),
  });

  const items = list.data?.items ?? [];

  return (
    <div className="page">
      <PageHead
        title="الرسائل المحفوظة"
        subtitle="تُكتب مرةً وتُدرَج بنقرة — في مُرسِل الملف وشات القنوات والكونسول"
        actions={
          canEdit ? (
            <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
              <Icon name="plus" size={14} />
              رسالة محفوظة
            </button>
          ) : undefined
        }
      />

      {list.data && items.length === 0 && (
        <EmptyState
          title="لا رسائل محفوظة بعد"
          text="أضف أول رسالة — مثل ملخّص الخدمات أو ردّ الترحيب — وستظهر للإدراج في كل قنواتها."
        />
      )}

      <div className="rating-cfg-grid">
        {TEMPLATE_CHANNEL_DEFS.map((ch) => {
          const mine = items.filter((t) => t.channel === ch.key);
          if (mine.length === 0) return null;
          return (
            <div key={ch.key} className="card">
              <div className="card-head">
                <h2 className="card-title">{ch.label}</h2>
                <span className="tiny mute">{mine.length}</span>
              </div>
              <div className="card-body">
                <div className="col" style={{ gap: 'var(--sp-2)' }}>
                  {mine.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="qm-saved-row"
                      style={t.isActive ? undefined : { opacity: 0.55 }}
                      onClick={() => canEdit && setEditing(t)}
                      title={canEdit ? 'تعديل' : undefined}
                    >
                      <span className="row" style={{ gap: 8, width: '100%' }}>
                        <strong className="small">{t.name}</strong>
                        {!t.isActive && <Badge kind="neutral">موقوفة</Badge>}
                      </span>
                      <span className="tiny mute">{t.body.slice(0, 90)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <TemplateModal
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function TemplateModal({
  template,
  onClose,
}: {
  template: Template | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: template?.name ?? '',
    channel: (template?.channel ?? 'WHATSAPP') as TemplateChannel,
    subject: template?.subject ?? '',
    body: template?.body ?? '',
    isActive: template?.isActive ?? true,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        channel: form.channel,
        subject: form.channel === 'EMAIL' ? form.subject.trim() || null : null,
        body: form.body.trim(),
        isActive: form.isActive,
        order: template?.order ?? 0,
      };
      return template
        ? api(`/callcenter/templates/${template.id}`, { method: 'PATCH', body })
        : api('/callcenter/templates', { method: 'POST', body });
    },
    onSuccess: () => {
      toast('حُفظت الرسالة');
      void queryClient.invalidateQueries({ queryKey: ['saved-messages'] });
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const valid = form.name.trim().length >= 2 && form.body.trim().length >= 2;

  return (
    <Modal
      title={template ? `تعديل «${template.name}»` : 'رسالة محفوظة جديدة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="sm-name">
            الاسم
          </label>
          <input
            id="sm-name"
            className="input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="ملخّص الخدمات"
            autoFocus
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="sm-channel">
            القناة
          </label>
          <select
            id="sm-channel"
            className="select"
            value={form.channel}
            onChange={(e) =>
              setForm((f) => ({ ...f, channel: e.target.value as TemplateChannel }))
            }
          >
            {TEMPLATE_CHANNEL_DEFS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {form.channel === 'EMAIL' && (
          <div className="field span-2">
            <label className="label" htmlFor="sm-subject">
              موضوع البريد
            </label>
            <input
              id="sm-subject"
              className="input"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            />
          </div>
        )}

        <div className="field span-2">
          <label className="label req" htmlFor="sm-body">
            نصّ الرسالة
          </label>
          <textarea
            id="sm-body"
            className="textarea"
            rows={5}
            maxLength={4000}
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            placeholder="أهلًا {الاسم}! هذا ملخّص خدماتنا…"
          />
        </div>
      </div>

      <label className="row mt-3" style={{ cursor: 'pointer', gap: 6 }}>
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
        />
        <span className="small">فعّالة — تظهر في قوائم الإدراج</span>
      </label>
    </Modal>
  );
}
